import React, { useState } from "react";
import {
	Button,
	Input,
	Checkbox,
	Text,
	makeStyles,
	tokens,
	Spinner,
} from "@fluentui/react-components";
import { Warning24Regular } from "@fluentui/react-icons";
import * as Sentry from "@sentry/react";
import { Modal } from "./Modal";
import { useAuth } from "../context/BaseAuthProvider";
import { apiFetch, API_BASE_URL } from "../lib/requestFunctions";

const useStyles = makeStyles({
	warning: {
		display: "flex",
		alignItems: "center",
		gap: tokens.spacingHorizontalS,
		backgroundColor: tokens.colorPaletteRedBackground1,
		color: tokens.colorPaletteRedForeground1,
		padding: tokens.spacingVerticalS,
		borderRadius: tokens.borderRadiusMedium,
		marginBottom: tokens.spacingVerticalM,
	},
	list: {
		margin: `${tokens.spacingVerticalS} 0`,
		paddingLeft: tokens.spacingHorizontalL,
		lineHeight: "1.6",
	},
	inputSection: {
		marginTop: tokens.spacingVerticalM,
		marginBottom: tokens.spacingVerticalS,
	},
	checkbox: {
		marginTop: tokens.spacingVerticalS,
		marginBottom: tokens.spacingVerticalS,
	},
	errorText: {
		color: tokens.colorPaletteRedForeground1,
		marginTop: tokens.spacingVerticalS,
	},
});

interface DeleteAccountModalProps {
	isOpen: boolean;
	onClose: () => void;
}

export const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
	isOpen,
	onClose,
}) => {
	const styles = useStyles();
	const { getToken, logout } = useAuth();
	const [confirmText, setConfirmText] = useState("");
	const [acknowledged, setAcknowledged] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const canDelete = confirmText === "DELETE" && acknowledged && !deleting;

	const handleDelete = async () => {
		setDeleting(true);
		setError(null);

		try {
			const token = getToken();
			const response = await apiFetch(`${API_BASE_URL}/user`, {
				method: "DELETE",
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});

			if (!response.ok) {
				throw new Error("Failed to delete account");
			}

			await logout();
		} catch (error) {
			Sentry.captureException(error, { extra: { operation: 'delete-account' } });
			setError("Failed to delete account. Please try again.");
			setDeleting(false);
		}
	};

	const handleClose = () => {
		if (deleting) return;
		setConfirmText("");
		setAcknowledged(false);
		setError(null);
		onClose();
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			title="Delete Your Account"
			actions={
				<>
					<Button
						appearance="secondary"
						onClick={handleClose}
						disabled={deleting}
					>
						Cancel
					</Button>
					<Button
						appearance="primary"
						onClick={handleDelete}
						disabled={!canDelete}
						icon={deleting ? <Spinner size="tiny" /> : undefined}
					>
						{deleting ? "Deleting..." : "Delete My Account"}
					</Button>
				</>
			}
		>
			<div className={styles.warning}>
				<Warning24Regular />
				<Text weight="semibold">
					This action is permanent and cannot be undone.
				</Text>
			</div>

			<Text weight="semibold">What will be deleted:</Text>
			<ul className={styles.list}>
				<li>Your account and profile</li>
				<li>All personal information</li>
				<li>Active subscription (cancelled immediately)</li>
				<li>Access to Cuntas MíTool</li>
			</ul>

			<div className={styles.inputSection}>
				<Text size={200}>Type DELETE to confirm:</Text>
				<Input
					value={confirmText}
					onChange={(_e, data) => setConfirmText(data.value)}
					placeholder="DELETE"
					disabled={deleting}
				/>
			</div>

			<Checkbox
				className={styles.checkbox}
				checked={acknowledged}
				onChange={(_e, data) =>
					setAcknowledged(data.checked === true)
				}
				label="I understand this action is permanent"
				disabled={deleting}
			/>

			{error && (
				<Text className={styles.errorText} size={200}>
					{error}
				</Text>
			)}
		</Modal>
	);
};

export default DeleteAccountModal;
